<template>
  <div :class="[{shown: shown}, 'order-summary-flyout-container']" @click="closeOnBackgroundClick($event)">
    <div class="order-summary-flyout">
      <div class="order-summary-flyout-header">
        <h2>Your Cart</h2>
        <button class="flyout-close-btn" @click="hideFlyout">
          <svg width="24px" height="24px" viewBox="0 0 24 24" xmlns:xlink="http://www.w3.org/1999/xlink">
            <g stroke="none" stroke-width="1" fill="none" fill-rule="evenodd">
              <g transform="translate(12.000000, 12.000000) rotate(45.000000) translate(-12.000000, -12.000000) translate(3.000000, 3.000000)" fill="#FF0000">
                <rect id="Rectangle" x="0" y="7" width="18" height="4"></rect>
                <rect id="Rectangle" x="7" y="0" width="4" height="18"></rect>
              </g>
            </g>
          </svg>
        </button>
      </div>

      <div class="order-summary-flyout-content">
        <div v-if="pricingAvailabilityChanged" class="info-message error-message">
          <div>Items in your cart have changed pricing or availability</div>
          <button @click="updateCartPricingAvailability" class="small-button">Update</button>
        </div>

        <table>
          <tr>
            <th class="upper">Product</th>
            <th class="upper">Qty</th>
            <th class="upper">Amt</th>
          </tr>
          <tr><td colspan='3'>
            <transition name='fade'>
              <div v-if="errorMessage" class="info-message error-message">{{ errorMessage }}</div>
            </transition>
          </td></tr>
          <tr v-for="bulkItem in bulkItems" :key="bulkItem.id">
            <td>{{ bulkItem.name }}</td>
            <td style="min-width: 110px">
              <input
                class="quantity-field"
                type="number"
                oninput="this.value = Math.abs(this.value)"
                v-model="bulkItem.quantity"
                :disabled="!editable"
                @change="updateNavQty"
                @input="addToStore(bulkItem, 'bulkProductLineItems')"
                @blur="resetBulk(bulkItem)"
              >
              <a v-if="editable && soilEditable" class="remove-item" @click="removeLineItem(bulkItem,'bulkProductLineItems')">
                <i class="fa fa-trash" />
              </a>
            </td>
            <td>{{ formatMoney(bulkItem.quantity * bulkItem.base_price) }}</td>
          </tr>
          <tr v-for="otherItem in otherItems" :key="otherItem.id">
            <td>{{ otherItem.name }}</td>
            <td>
              <input
                class="quantity-field"
                type="number"
                v-model="otherItem.quantity"
                :disabled="!editable"
                @change="updateNavQty"
                @input="addToStore(otherItem, 'otherProductLineItems')"
                @blur="resetOther(otherItem)"
              >
              <a v-if="editable" class="remove-item" @click="removeLineItem(otherItem,'otherProductLineItems')">
                <i class="fa fa-trash" />
              </a>
            </td>
            <td>{{ formatMoney(otherItem.quantity * otherItem.base_price) }}</td>
          </tr>
        </table>

        <hr>

        <div class="subtotal-section">
          <table>
            <tr>
              <th>Subtotal:</th>
              <td>${{ subTotal }}</td>
            </tr>
            <tr>
              <th>Discount:</th>
              <td>(${{ discount }})</td>
            </tr>
            <tr v-if="this.$store.state.wincode.discount > 0">
              <th>{{`${this.$store.state.wincode.name}:`}}</th>
              <td>(${{ discountCodeAmount }})</td>
            </tr>
            <tr>
              <th>Out of Delivery Zone Fee:</th>
              <td>${{ deliveryFee }}</td>
            </tr>
            <tr v-for="tax of orderTaxes" :key="tax.id">
              <th>{{ `${tax.name} ${formatMoney(tax.rate * 100)}% : ` }}</th>
              <td>${{ formatMoney(tax.total) }}</td>
            </tr>
          </table>
        </div>

        <div class="total-section">
          <span class="total-text">Total</span> (Taxes Incl.)
          <span class="total">${{ total }}</span>
        </div>

        <hr>

        <slot />
      </div>
    </div>

  </div>
</template>

<script>
export default {
  name: 'OrderSummary',

  props: {
    soilEditable: {
      default: true,
      type: Boolean,
    },
    editable: {
      default: true,
      type: Boolean,
    },
  },

  data() {
    return {
      bulkItems: [],
      otherItems: [],
      errorMessage: false,
    };
  },

  created() {
    this.bulkItems = this.duplicateObjectHash(this.storeBulkItems);
    this.otherItems = this.duplicateObjectHash(this.storeOtherItems);
  },

  watch: {
    storeBulkItems(newVal, oldVal) {
      if (newVal.length === oldVal.length) {
        // Don't show flyout on qty change
        this.hideFlyout();
      }
      // Duplicated to remove direct link to store attribute
      this.bulkItems = this.duplicateObjectHash(newVal);
    },

    storeOtherItems(newVal, oldVal) {
      if (newVal.length === oldVal.length) {
        // Don't show flyout on qty change
        this.hideFlyout();
      }
      // Duplicated to remove direct link to store attribute
      this.otherItems = this.duplicateObjectHash(newVal);
    },

    currentStepIndex() {
      // Automatically close the flyout on step change
      this.hideFlyout();
    },

    showPostalModal(isShown) {
      // Automatically close the flyout on restart
      if (isShown) this.hideFlyout();
    },

    shown(isShown) {
      // Prevent body scrolling if shown
      if (isShown) {
        document.body.style.overflow = 'hidden';
        return;
      }

      document.body.style.overflow = '';
    },
  },

  computed: {
    shown() {
      return this.$store.state.showFlyout;
    },

    showPostalModal() {
      return this.$store.state.postalModal.showModal;
    },

    storeBulkItems() {
      return this.$store.getters.bulkItemArray;
    },

    storeOtherItems() {
      return this.$store.getters.otherItemArray;
    },

    subTotal() {
      return this.formatMoney(this.$store.getters.subTotal);
    },

    taxableSubtotal() {
      return this.formatMoney(this.$store.getters.taxableSubtotal);
    },

    orderTaxes() {
      return this.$store.getters.orderTaxes;
    },

    discount() {
      return this.formatMoney(this.$store.getters.bagDiscounts);
    },

    deliveryFee() {
      return this.formatMoney(this.$store.state.delivery_charge);
    },

    discountCodeAmount() {
      return this.formatMoney(this.$store.state.wincode.discount);
    },

    total() {
      const totalTaxes = this.orderTaxes.reduce((total, item) => (total + this.numberParser(item.total)), 0);
      const total = (this.numberParser(this.subTotal) - this.numberParser(this.discount) - this.numberParser(this.discountCodeAmount)
        + this.numberParser(this.deliveryFee) + this.numberParser(totalTaxes));

      return this.formatMoney(total);
    },

    currentStepIndex() {
      return this.$store.state.orderStepIndex;
    },

    pricingAvailabilityChanged() {
      return this.$store.getters.pricingAvailabilityChanged;
    },
  },

  methods: {
    hideFlyout() {
      this.$store.commit('setProperties', { showFlyout: false });
    },

    showFlyout() {
      this.$store.commit('setProperties', { showFlyout: true });
    },

    addToStore(item, itemType) {
      if (item.quantity > 0) {
        this.$store.dispatch('addLineItem', { item, itemType });
      }
    },

    closeOnBackgroundClick(event) {
      if (event.target.classList.contains('order-summary-flyout-container')) {
        this.hideFlyout();
      }
    },

    resetBulk(item) {
      const { quantity } = item;

      if (quantity < 1 && this.soilEditable) {
        this.removeLineItem(item, 'bulkProductLineItems');
      } else {
        // Reset bulk items when they are not editable
        const errorMessage = (quantity < 1) ? 'Orders must have at least 1 soil product' : null;
        this.setErrorMessage(errorMessage);
        this.bulkItems = this.duplicateObjectHash(this.storeBulkItems);
      }
    },

    resetOther(item) {
      const { quantity } = item;

      // If a quantity is input that is less than 1 (ignore spaces)
      if (quantity < 1 && this.editable) {
        this.removeLineItem(item, 'otherProductLineItems');
      } else {
        // Reset other items when they are not editable
        this.otherItems = this.duplicateObjectHash(this.storeOtherItems);
      }
    },

    duplicateObjectHash(objectHash) {
      return Object.keys(objectHash).map((key) => { return { ...objectHash[key] }; });
    },

    setErrorMessage(message) {
      this.errorMessage = message;
      setTimeout(() => { this.errorMessage = false; }, 2000);
    },

    formatMoney(money) {
      return accounting.format(money, { precision: 2 });
    },

    numberParser(num) {
      const moneyNumber = accounting.format(num, { precision: 2 });
      return parseFloat(moneyNumber.replace(/\,/g,''), 10);
    },

    removeLineItem(item, type) {
      const data = {
        itemType: type,
        item,
      };
      this.$store.dispatch('removeLineItem', data);
    },

    /**
     * Updates quantity in Navigation menu cart icon
     */
    updateNavQty() {
      this.$store.dispatch('updateNavQty');
    },

    updateCartPricingAvailability() {
      this.$store.dispatch('updateCartPricingAvailability');
    },
  },
};
</script>

<style scoped>
hr {
  border-color: #009144;
}

table {
  width: 100%;
}

table th {
  text-align: left;
  width: 100%;
}

table th.upper {
  text-transform: uppercase;
}

table th:last-of-type,
table td:last-of-type {
  text-align: right;
}

.flyout-close-btn {
  height: 32px;
  width: 32px;
  color: red;
  font-size: 4.24rem;
  box-shadow: none;
  background-color: transparent;
  border: none;
  padding: 0;
  border-radius: 0;
  text-shadow: none;
  align-items: center;
  justify-content: center;
  line-height: 0;
}

.order-summary-flyout-container {
  position: fixed;
  background-color: hsla(0,0%,0%,0.62);
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  pointer-events: none;
  opacity: 0;
  transition: opacity .24s ease-in-out;
}

.order-summary-flyout-header {
  display: flex;
  background-color: hsl(0,0%,94%);
  align-items: center;
  justify-content: center;
  padding: 0 1.38rem;
}

.order-summary-flyout-content {
  flex: 1;
  overflow: auto;
  padding: 1.62em;
  padding-bottom: 6.85em;
}

.order-summary-flyout-header h2 {
  text-transform: uppercase;
  font-size: 1.62rem;
  text-align: center;
  flex: 1;
  margin: .38rem 0;
}

.order-summary-flyout-container.shown {
  pointer-events: initial;
  z-index: 1000;
  opacity: 1;
}

.order-summary-flyout {
  box-sizing: border-box;
  max-width: 480px;
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  background-color: white;
  transition: transform .24s ease-in-out;
  transform: translateX(100%);
  display: flex;
  flex-direction: column;
}

.order-summary-flyout-container.shown .order-summary-flyout {
  transform: translateX(0);
}

.quantity-field {
  border-radius: .38em;
  -webkit-appearance: none;
  appearance: none;
  color: inherit;
  background-color: transparent;
  border: 2px solid #009245;
  text-align: center;
  padding: .24em;
  max-width: 2.62em;
}

.quantity-field:disabled {
  border: none;
  margin: 0;
  padding: 0;
}

.remove-item {
  margin-left: .62em;
  cursor: pointer;
}

.remove-item:hover {
  color: black;
}

.order-summary ul li .summary-item {
  flex: 1;
}

.fa {
  display: inline;
}

.total-section {
  padding: 1em 0 0 0;
}

/* Vue transition classes - used with transition name: fade */
.fade-leave-active {
    transition: opacity .5s;
}

.fade-leave-to /* .fade-leave-active in <2.1.8 */ {
    opacity: 0;
}

@media (max-width: 480px) {
  .order-summary ul li {
    flex-direction: column;
    align-items: flex-start;
  }

  .order-summary ul li .price {
    width: 100%;
  }
}
</style>
